import { useStrings } from "@1js/localization-components";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  shorthands,
  Tooltip,
} from "@fluentui/react-components";
import { Dismiss32Regular } from "@fluentui/react-icons";
import * as React from "react";
import { CloseTooltip } from "./PromptDescription.web.string";
import type { PromptDetailBaseProps } from "./PromptDetail.web.data";
import { PromptDetailTitle } from "./PromptDetailTitle.web";
import { PromptPreview } from "./PromptPreview/PromptPreview.web";

const useStyles = makeStyles({
  dialog: {
    "--margin": "6.5rem",
    maxWidth: "calc(100% - var(--margin))",
    maxHeight: "calc(100% - var(--margin))",
    display: "flex",
    width: "52.875rem",
    ...shorthands.borderRadius("1.5rem"),
    ...shorthands.padding("3rem"),
    "@media (max-width: 540px)": {
      ...shorthands.borderRadius("0px"),
      ...shorthands.padding("24px"),
      maxWidth: "100vw",
      maxHeight: "100%",
    },
  },

  title: {
    paddingBottom: "1rem",
  },

  dialogContent: {
    height: "auto",
    overflowY: "auto",
    overflowX: "hidden",
    scrollbarWidth: "thin",
    ...shorthands.gap("2.5rem"),
    paddingRight: "2.5rem",

    "@media (max-width: 540px)": {
      maxHeight: "90vh", // Set a max height to enable scrolling
      overflowY: "auto",
      paddingRight: "0rem",
    },
  },
});

export type PromptDetailProps = PromptDetailBaseProps & {
  dialogOpen?: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleDialogClose: () => void;
  selectedGroupId?: string;
};

export const PromptDetailModal: React.FC<PromptDetailProps> = ({
  prompt,
  morePrompts,
  isBookmarkEnabled,
  isAgentPromptSavingEnabled,
  isPromptSavingEnabled,
  isPromptSharingEnabled,
  isPromptSharingViaEmailEnabled,
  isShowPromptAuthor,
  isPromptCopyLinkEnabled,
  isPromptLikingEnabled,
  dialogOpen,
  onBookmarkToggled,
  onShareClicked,
  handleDialogClose,
  setDialogOpen,
  onCopyClicked,
  onMoreLikeThisClicked,
  onDelete,
  onTryInAppBadgeAction,
  isPromptSharingToWorkgroupEnabled,
  onShareToWorkgroupClicked,
  groups,
  selectedGroupId,
  onRemoveFromWorkgroupClicked,
  isPromptRemovingFromWorkgroupEnabled,
}) => {
  const styles = useStyles();
  const localizations = useStrings({
    CloseTooltip,
  });

  if (!prompt) return null;

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(_event, data) => {
        if (!data.open) {
          handleDialogClose();
        }
        setDialogOpen(data.open);
      }}
    >
      <DialogSurface
        className={styles.dialog}
        role="dialog"
        aria-labelledby="dialog-title-id"
      >
        <DialogBody>
          <DialogTitle
            id="dialog-title-id"
            className={styles.title}
            action={
              <Tooltip
                content={localizations.CloseTooltip()}
                relationship="label"
              >
                <DialogTrigger action="close">
                  <Button
                    appearance="subtle"
                    aria-label="close"
                    icon={<Dismiss32Regular />}
                  />
                </DialogTrigger>
              </Tooltip>
            }
          >
            <PromptDetailTitle prompt={prompt} />
          </DialogTitle>
          <DialogContent id="dialog" className={styles.dialogContent}>
            <PromptPreview
              prompt={prompt}
              morePrompts={morePrompts}
              onMoreLikeThisClicked={onMoreLikeThisClicked}
              isBookmarked={prompt.UserActivity?.Favorite?.IsFavorite}
              isBookmarkEnabled={isBookmarkEnabled}
              isAgentPromptSavingEnabled={isAgentPromptSavingEnabled}
              isPromptSavingEnabled={isPromptSavingEnabled}
              isPromptSharingEnabled={isPromptSharingEnabled}
              isPromptSharingViaEmailEnabled={isPromptSharingViaEmailEnabled}
              isShowPromptAuthor={isShowPromptAuthor}
              isPromptCopyLinkEnabled={isPromptCopyLinkEnabled}
              isPromptLikingEnabled={isPromptLikingEnabled}
              onBookmarkToggled={onBookmarkToggled}
              onShareClicked={onShareClicked}
              onCopyClicked={onCopyClicked}
              onDelete={onDelete}
              onTryInAppBadgeAction={onTryInAppBadgeAction}
              isPromptSharingToWorkgroupEnabled={
                isPromptSharingToWorkgroupEnabled
              }
              onShareToWorkgroupClicked={onShareToWorkgroupClicked}
              groups={groups}
              selectedGroupId={selectedGroupId}
              onRemoveFromWorkgroupClicked={onRemoveFromWorkgroupClicked}
              isPromptRemovingFromWorkgroupEnabled={
                isPromptRemovingFromWorkgroupEnabled
              }
            />
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
};
